import { Eye, EyeOff } from 'lucide-react'
import { forwardRef, useEffect, useImperativeHandle, useRef, useState, type ComponentProps } from 'react'
import { useTranslation } from 'react-i18next'

import { TextInput } from './ui'
import { passwordToggle } from './passwordToggle'

type PasswordInputProps = Omit<ComponentProps<typeof TextInput>, 'type' | 'trailing'> & {
  /** Admin-set passwords start readable: hidden, a browser would offer the admin's own saved password. */
  initiallyShown?: boolean
}

/** A password field with its own show/hide button; it hides again whenever its form is submitted. */
export const PasswordInput = forwardRef<HTMLInputElement, PasswordInputProps>(function PasswordInput(
  { initiallyShown = false, ...rest },
  ref,
) {
  const { t } = useTranslation()
  const [shown, setShown] = useState(initiallyShown)
  const inner = useRef<HTMLInputElement>(null)
  useImperativeHandle(ref, () => inner.current as HTMLInputElement)

  useEffect(() => {
    const form = inner.current?.form
    if (!form) return
    const reset = () => setShown(initiallyShown)
    form.addEventListener('submit', reset)
    return () => form.removeEventListener('submit', reset)
  }, [initiallyShown])

  const toggle = passwordToggle(shown)
  const label = toggle.action === 'hide' ? t('password.hidePassword') : t('password.showPassword')
  const Icon = shown ? EyeOff : Eye
  return (
    <TextInput
      ref={inner}
      {...rest}
      type={toggle.type}
      trailing={
        <button
          type="button"
          onClick={() => setShown((v) => !v)}
          aria-pressed={toggle.pressed}
          aria-label={label}
          title={label}
          className="rounded p-1.5 text-slate-500 hover:text-slate-700 focus-visible:outline focus-visible:outline-2 focus-visible:outline-accent dark:text-slate-400 dark:hover:text-slate-200"
        >
          <Icon className="h-4 w-4" aria-hidden="true" />
        </button>
      }
    />
  )
})
